import type { ReactNode } from 'react';

import classNames from 'classnames';

import { Button, Icon, Skeleton, Stack, Text } from '@iconik-io/uad';

import styles from './selection_bar.module.css';

const sizeStyles = {
  s: styles.size_s,
  m: styles.size_m,
  l: styles.size_l,
};

export type SelectionBarProps = {
  selectedCount: number;
  onClearSelection: () => void;
  noSelectionLabel: ReactNode;
  selectionLabel: ReactNode;
  actions?: ReactNode;
  noSelectionActions?: ReactNode;
  size?: 's' | 'm' | 'l';
  /** Skeletons the no-selection label while the count is still loading. */
  isLoading?: boolean;
  fullWidth?: boolean;
  className?: string;
};

/**
 * The count above a selectable collection, which turns into a selection summary
 * once something is selected: a dismiss button, how many items are selected,
 * and any actions that apply to them.
 *
 * Presentational only, the caller owns the selection state.
 *
 * ```jsx
 * <SelectionBar
 *   selectedCount={selectedCount}
 *   onClearSelection={() => setSelectedKeys(new Set())}
 *   noSelectionLabel={formatCount(total, 'item')}
 *   selectionLabel={`${selectedCount} selected`}
 *   actions={<Button variant="destructive" size="s">Delete</Button>}
 *   noSelectionActions={<Button variant="secondary" size="s">Select all</Button>}
 * />
 * ```
 */
export function SelectionBar({
  selectedCount,
  onClearSelection,
  noSelectionLabel,
  selectionLabel,
  actions,
  noSelectionActions,
  size = 'm',
  isLoading = false,
  fullWidth,
  className,
}: SelectionBarProps) {
  const hasSelection = selectedCount > 0;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap="2xl"
      className={classNames(
        styles.bar,
        sizeStyles[size],
        {
          [styles.bar_full_width]: fullWidth,
          [styles.bar_active]: hasSelection,
        },
        className,
      )}
    >
      {hasSelection ? (
        <>
          <Stack direction="row" gap="s" alignItems="center">
            <Button
              variant="tertiary"
              size="s"
              round
              icon={<Icon name="deletion_clear" />}
              aria-label="Clear selection"
              onPress={onClearSelection}
            />
            <Text color="high_emphasis">{selectionLabel}</Text>
          </Stack>
          {actions}
        </>
      ) : (
        <>
          <Skeleton isLoading={isLoading} layout="inline">
            <Text color="high_emphasis">{noSelectionLabel}</Text>
          </Skeleton>
          {noSelectionActions}
        </>
      )}
    </Stack>
  );
}
